import React from "react";
import { Link } from "react-router-dom";
import { menuItems } from "../config/menuConfig";
import styles from "../styles/Footer.module.css";
import FaIcon from "./FaIcon";
import AdvertisingConsentButton from './AdvertisingConsentButton';
import { useGoogleCmp } from '../utils/googleCmp';
import { openConsentModal } from "../utils/consentPreferences";
import { GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES } from '../ads/googleInterstitialAttributes';

const eventSections = [
  {
    label: "Eventi",
    items: [
      { label: "ALMA Giochini Mondiali 2026", icon: "fas fa-trophy", href: "/mondiali/finale" },
    ],
  },
];

const Footer = () => {
  const cmp = useGoogleCmp();
  const currentYear = new Date().getFullYear();

  // Helper to render icon (supports both FA class strings and React components)
  const renderIcon = (icon) => {
    if (!icon) return null;
    if (typeof icon === 'string') {
      return <FaIcon className={icon} />;
    }
    const IconComponent = icon;
    return <IconComponent size={14} />;
  };

  // Filter out dividers and get only sections
  const sections = menuItems.filter(item => item.type === 'section');
  const footerSections = [...sections, ...eventSections];

  const renderFooterLink = (item, itemIdx) => {
    if (item.external) {
      return (
        <li key={itemIdx}>
          <a href={item.href} className={styles.footerLink} {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}>
            <span className={styles.linkIcon}>{renderIcon(item.icon)}</span>
            <span>{item.label}</span>
            {item.badge && <span className={styles.badge}>{item.badge}</span>}
          </a>
        </li>
      );
    }

    return (
      <li key={itemIdx}>
        <Link to={item.href} className={styles.footerLink} {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}>
          <span className={styles.linkIcon}>{renderIcon(item.icon)}</span>
          <span>{item.label}</span>
          {item.badge && <span className={styles.badge}>{item.badge}</span>}
        </Link>
      </li>
    );
  };

  return (
    <>
      <footer className={styles.footer} role="contentinfo" data-google-interstitial="false">
        <div className={styles.footerContent}>
          {/* Logo/Brand Section */}
          <div className={styles.brandSection}>
            <Link to="/" className={styles.logoLink} {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}>
              <span className={styles.logoText}>Giochini di Parole</span>
            </Link>
            <p className={styles.tagline}>
              Giochi quotidiani per allenare la mente e divertirsi con la lingua italiana.
            </p>
            <div className={styles.socialLinks}>
              <a 
                href="mailto:replies@giochinidiparole.com" 
                className={styles.socialLink}
                aria-label="Contattaci via email"
                {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}
              >
                <FaIcon className="fas fa-envelope"  />
              </a>
            </div>
          </div>

          {/* Links Sections */}
          <div className={styles.linksContainer}>
            {footerSections.map((section, idx) => (
              <div key={idx} className={styles.linkSection}>
                <h4 className={styles.sectionTitle}>{section.label}</h4>
                <ul className={styles.linkList}>
                  {section.items?.map(renderFooterLink)}
                  {section.label === 'Legale' && cmp.settingsAvailable && (
                    <li>
                      <AdvertisingConsentButton className={`${styles.footerLink} ${styles.footerAction}`}>
                        <span className={styles.linkIcon} aria-hidden="true">
                          <FaIcon className="fas fa-bullhorn" />
                        </span>
                        <span>Consenso pubblicitario</span>
                      </AdvertisingConsentButton>
                    </li>
                  )}
                  {section.label === 'Legale' && (
                    <li>
                      <button
                        type="button"
                        className={`${styles.footerLink} ${styles.footerAction}`}
                        onClick={openConsentModal}
                      >
                        <span className={styles.linkIcon}>
                          <FaIcon className="fas fa-cookie-bite" />
                        </span>
                        <span>Gestisci cookie</span>
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={styles.bottomBar}>
          <div className={styles.copyright}>
            © {currentYear === 2025 ? '2025' : `2025-${currentYear}`} Giochini di Parole. Tutti i diritti riservati.
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
